/**
 * Connecting push, through Firebase.
 *
 * The XYRA apps register with Firebase Cloud Messaging, and this workspace
 * sends to them with the project's own service account - the JSON Firebase
 * downloads from Project Settings. Paste it once, name a default topic, and a
 * push campaign is a message Firebase fans out to every subscribed device.
 *
 * The key is stored on the server, never returned here; a saved form with the
 * key box empty keeps the one that is there.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { isOffline } from '../../lib/storage.js';
import Icon from '../../lib/icons.jsx';
import { Spinner } from '../../components/Primitives.jsx';

export default function PushSetup() {
  const { toast } = useApp();

  const [saved, setSaved] = useState(null);
  const [key, setKey] = useState('');
  const [topic, setTopic] = useState('');
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(false);

  // The XYRA app device directory — the source for "App users" campaigns.
  const [src, setSrc] = useState(null);
  const [srcToken, setSrcToken] = useState('');

  const take = out => {
    setSaved(out);
    setTopic(out.topic || '');
    setKey('');
  };

  useEffect(() => {
    if (isOffline()) return;
    api.push.setup().then(take).catch(e => toast(e.message));
    api.push.source().then(s => setSrc(s)).catch(() => setSrc({ connected: false }));
  }, [toast]);

  const testSrc = () => run('testsrc', async () => {
    const out = await api.push.testSource({ token: srcToken || undefined });
    toast(`App users — ${out.reason}`);
    setSrc(s => ({ ...(s || {}), lastTest: { ok: true, reason: out.reason, at: new Date().toISOString() } }));
  });

  const saveSrc = () => run('savesrc', async () => {
    if (!srcToken.trim() && !(src && src.connected)) throw new Error('Paste the device API token.');
    const out = await api.push.saveSource({ token: srcToken || undefined });
    setSrc(out);
    setSrcToken('');
    toast('Saved');
  });

  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (e) { toast(e.message); }
    setBusy('');
  };

  const test = () => run('test', async () => {
    const out = await api.push.testSetup({ serviceAccount: key || undefined });
    toast(`Firebase — ${out.reason}`);
    setSaved(s => ({ ...(s || {}), lastTest: { ok: true, reason: out.reason, at: new Date().toISOString() } }));
  });

  const save = () => run('save', async () => {
    if (!key.trim() && !(saved && saved.connected)) throw new Error('Paste the Firebase service-account JSON.');
    take(await api.push.saveSetup({ serviceAccount: key || undefined, topic }));
    toast('Saved');
  });

  if (isOffline()) return <p className="muted">Sign in to connect push.</p>;
  if (!saved) return <p className="muted"><Spinner /> Loading…</p>;

  const connected = !!(saved.connected && saved.enabled && !(saved.lastTest && saved.lastTest.ok === false));
  const showForm = open || !connected || !saved.topic;

  return (
    <div className="wasetup">
      <div className={`wastatus ${connected ? 'ok' : 'off'}`}>
        <span className="wadot" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>{connected ? `Connected to ${saved.projectId || 'Firebase'}` : 'Not connected'}</b>
          <small>
            {connected
              ? (saved.topic ? `Default topic: ${saved.topic}` : 'Connected — set a default topic below')
              : 'Paste the Firebase service-account key to send push to your apps.'}
          </small>
        </div>
        {connected && saved.topic ? (
          <button type="button" className="btn sm" onClick={() => setOpen(o => !o)}>
            <Icon name="gear" />{open ? 'Hide' : 'Details'}
          </button>
        ) : null}
      </div>

      {showForm ? (
        <>
          <h5 className="wah">Firebase service account</h5>
          <p className="fhint" style={{ marginTop: 0 }}>
            Firebase Console → ⚙ Project Settings → <b>Service Accounts</b> → <b>Generate new private
            key</b>. Open the downloaded file and paste all of it here. It stays on the server.
          </p>
          <textarea
            className="pushkey"
            rows="4"
            value={key}
            placeholder={saved.connected ? '•••••••• saved — paste again only to replace it' : '{ "type": "service_account", "project_id": "…", … }'}
            onChange={e => setKey(e.target.value)}
            spellCheck="false"
          />

          <h5 className="wah">Default topic</h5>
          <div className="form">
            <div className="f wide">
              <label>Topic every device subscribes to</label>
              <input
                value={topic}
                placeholder="xyra-all"
                onChange={e => setTopic(e.target.value.trim())}
              />
              <div className="fhint">
                The name your apps subscribe users to. A campaign sends to this unless it names
                a different one. Ask your app developer what it is.
              </div>
            </div>
          </div>

          <div className="rowb" style={{ marginTop: 10 }}>
            <button type="button" className="btn sm" onClick={test} disabled={!!busy}>
              {busy === 'test' ? <><Spinner />Testing…</> : <><Icon name="zap" />Test key</>}
            </button>
            <button type="button" className="btn sm primary" onClick={save} disabled={!!busy}>
              {busy === 'save' ? <><Spinner />Saving…</> : <><Icon name="check" />Save</>}
            </button>
            {saved.lastTest ? (
              <span className="fhint" style={{ margin: 0 }}>{saved.lastTest.reason}</span>
            ) : null}
          </div>
        </>
      ) : null}

      {/* The app user list — so a campaign can send to real app users, not just a topic. */}
      <div className={`wastatus ${src && src.connected ? 'ok' : 'off'}`} style={{ marginTop: 16 }}>
        <span className="wadot" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>{src && src.connected ? 'App user list connected' : 'App user list not connected'}</b>
          <small>
            {src && src.connected
              ? (src.lastTest && src.lastTest.reason ? src.lastTest.reason : 'Campaigns can target "App users".')
              : 'Add the XYRA device API token to send push to real app users.'}
          </small>
        </div>
      </div>

      <h5 className="wah">App user list (device API)</h5>
      <p className="fhint" style={{ marginTop: 0 }}>
        The XYRA apps register every logged-in device with the app backend. Paste the API token so a
        campaign can send to those users. Get it from the app developer. It stays on the server.
      </p>
      <textarea
        className="pushkey"
        rows="2"
        value={srcToken}
        placeholder={src && src.connected ? '•••••••• saved — paste again only to replace it' : 'xyra_… API token'}
        onChange={e => setSrcToken(e.target.value)}
        spellCheck="false"
      />
      <div className="rowb" style={{ marginTop: 10 }}>
        <button type="button" className="btn sm" onClick={testSrc} disabled={!!busy}>
          {busy === 'testsrc' ? <><Spinner />Testing…</> : <><Icon name="zap" />Test token</>}
        </button>
        <button type="button" className="btn sm primary" onClick={saveSrc} disabled={!!busy}>
          {busy === 'savesrc' ? <><Spinner />Saving…</> : <><Icon name="check" />Save</>}
        </button>
        {src && src.lastTest ? (
          <span className="fhint" style={{ margin: 0 }}>{src.lastTest.reason}</span>
        ) : null}
      </div>
    </div>
  );
}
